import React from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useSdk } from '../../sdkContext';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { denormalisedResponseEntities } from '../../util/data';

// Importy komponentów
import { 
  Page,
  UserNav,
  LayoutSideNavigation,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  LayoutWrapperBusinessSideNav,
  Footer,
  NamedLink
} from '../../components';
import { TopbarContainer } from '../../containers';
import AutocompleteSelect from '../../components/AutocompleteSelect/AutocompleteSelect';

import css from './ManageSubscriptionListingsPage.css';

const SubscriptionListingsPage = props => {
  const { intl } = props;
  const sdk = useSdk();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { providerId } = useParams();

  const currentUserProviders = useSelector(state => state.user.userProviders.filter(p => p.attributes.deliveryToHome)) || [];
  const providersNotBanned = currentUserProviders.filter(p => p.attributes.state !== 'banned');
  const currentProvider = currentUserProviders.find(p => p.id.uuid === providerId);

  // 1. Mutacja do zmiany stanu (Otwórz/Zamknij)
  const { mutate: toggleState, status, variables } = useMutation(
    ({ id, newState }) => sdk.newSdk.subscriptionListings.update({ id, state: newState }),
    {
      onSuccess: () => {
        // Unieważnienie zapytania spowoduje pobranie świeżych danych
        queryClient.invalidateQueries(['subscriptionListings', providerId]);
      },
      onError: (err) => alert("Błąd: " + err.message)
    }
  );

  const handleProviderChange = option => {
    if (option && option.value) {
      history.push(`/subscription-listings/${option.value}`);
    }
  };

  const { data: rawResponse, isLoading } = useQuery(
    ['subscriptionListings', providerId],
    // Używamy state: 'all' aby widzieć też zamknięte oferty do ponownego otwarcia
    () => sdk.newSdk.subscriptionListings.query({ providerId, state: 'all', include: ['images'] }),
    { 
      enabled: !!providerId,
      keepPreviousData: true 
    }
  );

  const listings = rawResponse ? denormalisedResponseEntities(rawResponse) : [];
  const providerName = currentProvider?.attributes?.name || 'Sklep';

  return (
    <Page title={intl.formatMessage({ id: 'SubscriptionListingsPage.title' }, { name: providerName })}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="ManageSubscriptionListingsPage" />
          <UserNav selectedPageName="ManageSubscriptionListingsPage" />
        </LayoutWrapperTopbar>

        <LayoutWrapperBusinessSideNav currentTab="ManageSubscriptionListingsPage" />

        <LayoutWrapperMain className={css.main}>
          <div className={css.content}>
            
            <div className={css.providerSelectorWrapper}>
              <AutocompleteSelect
                inputValue={{ value: providerId, label: providerName }}
                onChange={handleProviderChange}
                label={intl.formatMessage({ id: "ManageListingsPage.provider" })}
                options={providersNotBanned.map(p => ({ 
                  value: p.id.uuid, 
                  label: p.attributes.name 
                }))}
                placeholder={intl.formatMessage({ id: "Console.select" })}
              />
            </div>

            <div className={css.header}>
              <div className={css.titleWrapper}>
                <h1 className={css.title}>
                  <FormattedMessage id="SubscriptionListingsPage.heading" />
                </h1>
              </div>
              
              <NamedLink 
                name="EditSubscriptionListingPage" 
                className={css.addBtn}
                // Przekazujemy typ 'new' oraz providerId w URLu
                params={{ slug: 'slug', type: 'new', id: '0000-0000-0000-0000', tab: 'new' }}
                to={{ search: `?providerId=${providerId}` }}
              >
                <FormattedMessage id="SubscriptionListingsPage.addBtn" />
              </NamedLink>
            </div>

            {isLoading ? (
              <div className={css.loading}>
                <FormattedMessage id="SubscriptionListingsPage.loading" />
              </div>
            ) : (
              <div className={css.tableWrapper}>
                <table className={css.table}>
                  <thead>
                    <tr>
                      <th><FormattedMessage id="SubscriptionListingsPage.thProduct" /></th>
                      <th><FormattedMessage id="SubscriptionListingsPage.thPrice" /></th>
                      <th><FormattedMessage id="SubscriptionListingsPage.thMinDuration" /></th>
                      <th><FormattedMessage id="SubscriptionListingsPage.thInventory" /></th>
                      <th><FormattedMessage id="SubscriptionListingsPage.thStatus" /></th>
                      <th><FormattedMessage id="SubscriptionListingsPage.thActions" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map(listing => {
                      const { title, slug, price, minDurationMonths, managedAmount, availableAmount, state } = listing.attributes;
                      const firstImage = listing.images?.[0];
                      const thumbnailUrl = firstImage?.attributes?.variants?.['square-small']?.url;
                      const isToggling = status === 'loading' && variables?.id === listing.id.uuid;

                      return (
                        <tr key={listing.id.uuid}>
                          <td className={css.productCell}>
                            <div className={css.productInfoWrapper}>
                              {thumbnailUrl ? (
                                <img src={thumbnailUrl} alt={title} className={css.thumbnail} />
                              ) : (
                                <div className={css.thumbnailPlaceholder} />
                              )}
                              <div className={css.titleText}>
                                <span className={css.productTitle}>{title}</span>
                                <span className={css.productSlug}>{slug}</span>
                              </div>
                            </div>
                          </td>
                          <td className={css.price}>
                            {formatMoney(intl, price)}
                          </td>
                          <td>
                            <FormattedMessage id="SubscriptionListingsPage.months" values={{ count: minDurationMonths }} />
                          </td>
                          <td className={css.inventoryCell}>
                            <div className={css.inventoryInfo}>
                              <span className={css.managed}>
                                <FormattedMessage id="SubscriptionListingsPage.managed" values={{ count: managedAmount }} />
                              </span>
                              <span className={css.available}>
                                <FormattedMessage id="SubscriptionListingsPage.available" values={{ count: availableAmount }} />
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={`${css.statusBadge} ${css[state]}`}>
                              <FormattedMessage id={`SubscriptionListingsPage.state.${state}`} />
                            </span>
                          </td>
                          <td className={css.actionsCell}>
                            <div className={css.actionButtons}>
                              <button 
                                className={css.editBtn}
                                onClick={() => history.push(`/sl/slug/${listing.id.uuid}/edit/details`)}
                              >
                                <FormattedMessage id="SubscriptionListingsPage.editBtn" />
                              </button>

                              {state === 'published' ? (
                                <button 
                                  className={css.closeOfferBtn}
                                  disabled={isToggling}
                                  onClick={() => toggleState({ id: listing.id.uuid, newState: 'closed' })}
                                >
                                  {isToggling ? '...' : <FormattedMessage id="SubscriptionListingsPage.closeBtn" />}
                                </button>
                              ) : (
                                <button 
                                  className={css.openOfferBtn}
                                  disabled={isToggling}
                                  onClick={() => toggleState({ id: listing.id.uuid, newState: 'published' })}
                                >
                                  {isToggling ? '...' : <FormattedMessage id="SubscriptionListingsPage.openBtn" />}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {listings.length === 0 && (
                  <div className={css.emptyState}>
                    <FormattedMessage id="SubscriptionListingsPage.emptyState" />
                  </div>
                )}
              </div>
            )}
          </div>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer/>
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

export default injectIntl(SubscriptionListingsPage);